export const defaultPageSizeForReport = 100;

export const usersSummaryQueryName = `GET_USERS_FOR_COMPANY`;
export const usersSummaryPageQuery = `
    query ${usersSummaryQueryName}(
        $Name: String
        $Email: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $RoleIds: [String]
        $CompanyId: ID
    ) {
        GetUsersForCompany(
            Filter: {
                Name: $Name
                Email: $Email
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                RoleIds: $RoleIds
                CompanyId: $CompanyId
            }
        ) {
            CompanyUserRoles {
                CompanyUserRoleId
                User {
                    GivenName
                    FamilyName
                    Email
                }
                Role {
                    Name
                }
            }
        }
    }
`;

export const contactFragmentString = `
    FirstName
    LastName
    AddressLine1
    AddressLine2
    City
    State
    Postcode
    Country
    Email
    MobileNumber
`;

export const attachmentFragmentString = `
    FileId
    IsPublic
    Title
`;

export const agedTrialBalanceFromCreateFragmentString = `
    Current
    ThirtyDays
    SixtyDays
    NinetyPlusDays
`;

export const agedTrialBalanceFromDueFragmentString = `
    Current
    ThirtyDays
    SixtyDays
    NinetyPlusDays
    NotDue
`;

export const customerFragmentString = `
    CustomerCode @skip(if: $IsCloudImportType)
    CompanyName
    IsVip
    DisplayName
    Contacts {
        ${contactFragmentString}
    }
    AgedTrialBalance {
        TotalOwing
        TotalOverdue
        AgedTrialBalanceFromCreate {
            ${agedTrialBalanceFromCreateFragmentString}
        }
        AgedTrialBalanceFromDue {
            ${agedTrialBalanceFromDueFragmentString}
        }
    }
    Uri
    CustomFields {
        Name
        Value
    }
    Attachments {
        ${attachmentFragmentString}
    }
`;

export const remittanceAdviceInvoicesFragmentString = `
    Invoice {
        Id
        CompanyId
        InvoiceCode
        Number
        CreatedDate
        LocalCreatedDate
        DueDate
        LocalDueDate
        OriginalAmount
        AmountOwing
        State
        SettledDate
        LocalSettledDate
        Type
        GlCode
        Customer {
            ${customerFragmentString}
        }
        AgedTrialBalanceFromCreate {
            ${agedTrialBalanceFromCreateFragmentString}
        }
        AgedTrialBalanceFromDue {
            ${agedTrialBalanceFromDueFragmentString}
        }
        Uri
        CustomFields {
            Name
            Value
        }
        Attachments {
           ${attachmentFragmentString}
        }
    }
    CustomerId
    Number
    Amount
`;

export const remittanceAdviceCreditsFragmentString = `
    Credit {
        Id
        CreditCode
        Number
        CreatedDate
        LocalCreatedDate
        AvailableDate
        LocalAvailableDate
        OriginalAmount
        AmountAvailable
        State
        ClosedDate
        Customer {
            ${customerFragmentString}
        }
        AgedTrialBalanceFromCreate {
            ${agedTrialBalanceFromCreateFragmentString}
        }
        AgedTrialBalanceFromDue {
            ${agedTrialBalanceFromDueFragmentString}
        }
        Uri
        CustomFields {
            Name
            Value
        }
        Attachments {
            ${attachmentFragmentString}
        }
    }
    InvoiceId
    CustomerId
    Number
    Amount
`;


export const customersSummaryQueryName = `GET_CUSTOMERS_FOR_COMPANY`;
export const customersSummaryPageQuery = `
    query ${customersSummaryQueryName}(
        $Customer: String
        $CustomerCountry: String
        $CustomerState: String
        $InclusiveContact: Boolean
        $Contact: Int
        $ATBType: Int
        $AmountType: String
        $AmountValue: String
        $AmountOperator: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
        $CustomFieldFilters: String
        $CustomFieldSort: String
        $IsCloudImportType: Boolean = false
        $UsingCustomerWorkflow: Boolean = false
        $IsMultipleWorkflow: Boolean = false
        $IsVip: Boolean
    ) {
        GetCustomersForCompany(
            Filter: {
                Customer: $Customer
                CustomerCountry: $CustomerCountry
                CustomerState: $CustomerState
                InclusiveContact: $InclusiveContact
                Contact: $Contact
                ATBType: $ATBType
                AmountType: $AmountType
                AmountValue: $AmountValue
                AmountOperator: $AmountOperator
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
                CustomFieldFilters: $CustomFieldFilters
                CustomFieldSort: $CustomFieldSort
                IsVip: $IsVip
            }
        ) {
            Customers {
                ${customerFragmentString}
                TicketCount
                ConversationLine {
                    Content
                    CreatedDateTime
                }
                LastCommunication @include(if: $UsingCustomerWorkflow) {
                    StateName
                    SentDateTime
                    WorkflowName @include(if: $IsMultipleWorkflow)
                    DeliveryDetails {
                        Type
                        Comment
                    }
                }
                CurrentTask @include(if: $UsingCustomerWorkflow) {
                    Id
                    Automation
                    ActionDate
                    Type
                    Workflow {
                        StateName
                        WorkflowName @include(if: $IsMultipleWorkflow)
                    }
                    Ticket {
                        TicketOption {
                            Reason
                            CaptureMessage
                        }
                        TicketNumber
                    }
                    State
                }
                IsVip
            }
        }
    }
`;

export const invoicesSummaryQueryName = `GET_INVOICES_FOR_COMPANY`;
export const invoicesSummaryPageQuery = `
    query ${invoicesSummaryQueryName}(
        $InvoiceState: String
        $InvoiceNumber: String
        $Customer: String
        $CustomerCode: String
        $CustomerCountry: String
        $CustomerState: String
        $CreatedDateMin: String
        $CreatedDateMax: String
        $DueDateMin: String
        $DueDateMax: String
        $AmountType: String
        $AmountValue: String
        $AmountOperator: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $InvoiceIds: [ID]
        $CompanyId: ID
        $CustomFieldFilters: String
        $CustomFieldSort: String
        $IsCloudImportType: Boolean = false
        $IsMultipleWorkflow: Boolean = false
        $UsingCustomerWorkflow: Boolean = false
        $IsBlocked: Boolean
    ) {
        GetInvoicesForCompany(
            Filter: {
                InvoiceState: $InvoiceState
                InvoiceNumber: $InvoiceNumber
                Customer: $Customer
                CustomerCode: $CustomerCode
                CustomerCountry: $CustomerCountry
                CustomerState: $CustomerState
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                DueDateMin: $DueDateMin
                DueDateMax: $DueDateMax
                AmountType: $AmountType
                AmountValue: $AmountValue
                AmountOperator: $AmountOperator
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                InvoiceIds: $InvoiceIds
                CompanyId: $CompanyId
                CustomFieldFilters: $CustomFieldFilters
                CustomFieldSort: $CustomFieldSort
                IsBlocked: $IsBlocked
            }
        ) {
            Invoices {
                Number
                CreatedDate
                LocalCreatedDate
                DueDate
                LocalDueDate
                OriginalAmount
                AmountOwing
                State
                SettledDate
                LocalSettledDate
                Customer {
                    CustomerCode @skip(if: $IsCloudImportType)
                    CompanyName
                    DisplayName
                    Contacts {
                        ${contactFragmentString}
                    }
                    CustomFields {
                        Name
                        Value
                    }
                    ConversationLine {
                        Content
                    }
                    CurrentTask @include(if: $UsingCustomerWorkflow) {
                        Id
                        Automation
                        ActionDate
                        Type
                        Workflow {
                            StateName
                            WorkflowName @include(if: $IsMultipleWorkflow)
                        }
                        Ticket {
                            TicketOption {
                                Reason
                                CaptureMessage
                            }
                            TicketNumber
                        }
                        State
                    }
                }
                ConversationLine {
                    Content
                }
                CustomFields {
                    Name
                    Value
                }
                Attachments {
                    ${attachmentFragmentString}
                }
                LastCommunication {
                    StateName
                    SentDateTime
                    WorkflowName @include(if: $IsMultipleWorkflow)
                    DeliveryDetails {
                        Type
                        Comment
                    }
                }
                CurrentTask {
                    Id
                    Automation
                    ActionDate
                    Type
                    Workflow {
                        StateName
                        WorkflowName @include(if: $IsMultipleWorkflow)
                    }
                    Ticket {
                        TicketOption {
                            Reason
                            CaptureMessage
                            NonBlocking
                        }
                        TicketNumber
                        ConversationLine {
                            Content
                        }
                    }
                    State
                }
                IsBlocked
            }
        }
    }
`;

export const creditsSummaryQueryName = `GET_CREDITS_FOR_COMPANY`;
export const creditsSummaryPageQuery = `
    query ${creditsSummaryQueryName}(
        $CreditNumber: String
        $Customer: String
        $CustomerCode: String
        $CreatedDateMin: String
        $CreatedDateMax: String
        $AvailableDateMin: String
        $AvailableDateMax: String
        $AmountType: String
        $AmountValue: String
        $AmountOperator: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CreditState: String
        $CreditIds: [ID]
        $CustomFieldFilters: String
        $CustomFieldSort: String
        $CompanyId: ID
    ) {
        GetCreditsForCompany(
            Filter: {
                CreditNumber: $CreditNumber
                Customer: $Customer
                CustomerCode: $CustomerCode
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                AvailableDateMin: $AvailableDateMin
                AvailableDateMax: $AvailableDateMax
                AmountType: $AmountType
                AmountValue: $AmountValue
                AmountOperator: $AmountOperator
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CreditState: $CreditState
                CreditIds: $CreditIds
                CustomFieldFilters: $CustomFieldFilters
                CustomFieldSort: $CustomFieldSort
                CompanyId: $CompanyId
            }
        ) {
            Credits {
                Id
                CreditCode
                Number
                CreatedDate
                LocalCreatedDate
                AvailableDate
                LocalAvailableDate
                OriginalAmount
                AmountAvailable
                State
                ClosedDate
                Customer {
                    Id
                    CustomerCode
                    CompanyName
                    DisplayName
                    Contacts {
                        ${contactFragmentString}
                    }
                    CustomFields {
                        Name
                        Value
                    }
                }
                CustomFields {
                    Name
                    Value
                }
                Attachments {
                    ${attachmentFragmentString}
                }
            }
        }
    }
`;

export const taskHistorySummaryQueryName = `GET_TASK_BATCH_HISTORY_FOR_COMPANY`;
export const taskHistorySummaryPageQuery = `
    query ${taskHistorySummaryQueryName}(
        $DisplayName: String
        $CustomerCode: String
        $InvoiceNumber: String
        $Type: Int
        $Status: Int
        $ActionDateMin: String
        $ActionDateMax: String
        $ActionedByUserId: ID
        $ActionedByEmailAddress: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
    ) {
        GetTaskBatchHistoryForCompany(
            Filter: {
                DisplayName: $DisplayName
                CustomerCode: $CustomerCode
                InvoiceNumber: $InvoiceNumber
                Type: $Type
                Status: $Status
                ActionDateMin: $ActionDateMin
                ActionDateMax: $ActionDateMax
                ActionedByUserId: $ActionedByUserId
                ActionedByEmailAddress: $ActionedByEmailAddress
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
            }
        ) {
            TaskBatches {
                BatchId
                CompanyId
                UserId
                Type
                State
                Automation
                CreatedDateTime
                BatchCount
                User {
                    GivenName
                    FamilyName
                }
                AssignedEmailAddress
            }
        }
    }
`;

export const historyDeliveryReportQueryName = `GET_HISTORY_DELIVERY_REPORT`;
export const historyDeliveryReportPageQuery = `
    query ${historyDeliveryReportQueryName}(
        $BatchId: ID
        $CompanyId: ID
        $DeliveryStatus: Int
        $DeliveryType: Int
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CreatedDateMin: String
        $CreatedDateMax: String
    ) {
        GetDeliveryHistoryReportForCompany(
            Filter: {
                BatchId: $BatchId
                CompanyId: $CompanyId
                DeliveryStatus: $DeliveryStatus
                DeliveryType: $DeliveryType
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
            }
        ) {
            TaskBatchGroups {
                CustomerCode
                CompanyName
                CommunicationId
                StateName
                State
                DeliveryDetails {
                    Contact
                    Type
                    Comment
                    State
                    Reason
                }
            }
        }
    }
`;

export const ticketOptionsFragmentString = `
    Reason
    CaptureMessage
    Type
`;
export const customerAssistanceSummaryQueryName = `GET_TICKETS_FOR_COMPANY`;
export const customerAssistanceSummaryPageQuery = `
    query ${customerAssistanceSummaryQueryName}(
        $DisplayName: String
        $CustomerCode: String
        $CreatedDateMin: String
        $CreatedDateMax: String
        $ClosedDateMin: String
        $ClosedDateMax: String
        $LastChatDateMin: String
        $LastChatDateMax: String
        $Status: Int
        $Action: Int
        $CustomFieldFilters: String
        $TicketOptionReasons: [String]
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
        $IsCloudImportType: Boolean = false
        $CustomerCountry: String
        $CustomerState: String
        $AssignedUserId: ID
        $AssignedEmailAddress: String
    ) {
        GetTicketsForCompany(
            Filter: {
                DisplayName: $DisplayName
                CustomerCode: $CustomerCode
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                ClosedDateMin: $ClosedDateMin
                ClosedDateMax: $ClosedDateMax
                LastChatDateMin: $LastChatDateMin
                LastChatDateMax: $LastChatDateMax
                Status: $Status
                Action: $Action
                CustomFieldFilters: $CustomFieldFilters
                TicketOptionReasons: $TicketOptionReasons
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
                CustomerCountry: $CustomerCountry
                CustomerState: $CustomerState
                AssignedUserId: $AssignedUserId
                AssignedEmailAddress: $AssignedEmailAddress
            }
        ) {
            Tickets {
                TicketNumber
                State
                Details
                ResolvedReason
                ResolvedDateTime
                CreatedDateTime
                TicketOption {
                    ${ticketOptionsFragmentString}
                }
                Customer {
                    ${customerFragmentString}
                }
                Attachments {
                    ${attachmentFragmentString}
                }
                ConversationLine {
                    Content
                }
                AssignedUser {
                    GivenName
                    FamilyName
                }
                AssignedEmailAddress
            }
        }
    }
`;

export const activeTasksSummaryQueryName = `GET_TASKS_FOR_COMPANY`;
export const activeTasksSummaryPageQuery = `
    query ${activeTasksSummaryQueryName}(
        $DisplayName: String
        $CustomerCode: String
        $InvoiceNumber: String
        $InvoiceDueDateMin: AWSDateTime
        $InvoiceDueDateMax: AWSDateTime
        $AmountValue: Float
        $AmountOperator: String
        $ActionDateMin: AWSDateTime
        $ActionDateMax: AWSDateTime
        $CreatedDateMin: AWSDateTime
        $CreatedDateMax: AWSDateTime
        $Type: Int
        $Automation: Int
        $Contact: Int
        $Status: Int
        $TaskIds: [ID]
        $ExcludeTasks: Boolean
        $SortField: String
        $ThenSortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
        $CustomFieldFilters: String
        $CustomFieldSort: String
        $AssignedUserId: ID
        $AssignedEmailAddress: String
        $IsCloudImportType: Boolean = false
        $IsMultipleWorkflow: Boolean = false
        $WorkflowFilters: [WorkflowFilterInput]
        $CustomerCountry: String
        $CustomerState: String
        $Action: Int
    ) {
        GetTasksForCompany(
            Filter: {
                DisplayName: $DisplayName
                CustomerCode: $CustomerCode
                InvoiceNumber: $InvoiceNumber
                InvoiceDueDateMin: $InvoiceDueDateMin
                InvoiceDueDateMax: $InvoiceDueDateMax
                AmountValue: $AmountValue
                AmountOperator: $AmountOperator
                ActionDateMin: $ActionDateMin
                ActionDateMax: $ActionDateMax
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                Type: $Type
                Automation: $Automation
                Contact: $Contact
                Status: $Status
                TaskIds: $TaskIds
                ExcludeTasks: $ExcludeTasks
                SortField: $SortField
                ThenSortField: $ThenSortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
                CustomFieldFilters: $CustomFieldFilters
                CustomFieldSort: $CustomFieldSort
                AssignedUserId: $AssignedUserId
                AssignedEmailAddress: $AssignedEmailAddress
                WorkflowFilters: $WorkflowFilters
                CustomerCountry: $CustomerCountry
                CustomerState: $CustomerState
                Action: $Action
            }
        ) {
            Tasks {
                ActionDate
                Automation
                ConversationLine {
                    Content
                }
                Customer {
                    Contacts {
                        ${contactFragmentString}
                    }
                    CustomerCode @skip(if: $IsCloudImportType)
                    CompanyName
                    DisplayName
                    CustomFields {
                        Name
                        Value
                    }
                    ConversationLine {
                        Content
                    }
                    PayerState
                }
                Invoice {
                    Number
                    AmountOwing
                    CustomFields {
                        Name
                        Value
                    }
                    ConversationLine {
                        Content
                    }
                }
                Type
                Workflow {
                    WorkflowId
                    StateName
                    DeliveryMethod
                    WorkflowName @include(if: $IsMultipleWorkflow)
                }
                State
                Ticket {
                    Id
                    TicketNumber
                    TicketOption {
                        Id
                        Type
                        Reason
                    }
                    ConversationLine {
                        Content
                    }
                }
                AssignedUser {
                    GivenName
                    FamilyName
                }
                AssignedEmailAddress
            }
        }
    }
`;

export const inactiveTasksSummaryQueryName = `GET_INACTIVE_TASKS_FOR_COMPANY`;
export const inactiveTasksSummaryPageQuery = `
    query ${inactiveTasksSummaryQueryName}(
        $DisplayName: String
        $CustomerCode: String
        $InvoiceNumber: String
        $AmountValue: Float
        $AmountOperator: String
        $ActionDateMin: AWSDateTime
        $ActionDateMax: AWSDateTime
        $CreatedDateMin: AWSDateTime
        $CreatedDateMax: AWSDateTime
        $Type: Int
        $Automation: Int
        $Contact: Int
        $Status: Int
        $TaskIds: [ID]
        $ExcludeTasks: Boolean
        $SortField: String
        $ThenSortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
        $CustomFieldFilters: String
        $CustomFieldSort: String
        $AssignedUserId: ID
        $AssignedEmailAddress: String
        $IsCloudImportType: Boolean = false
        $IsMultipleWorkflow: Boolean = false
        $WorkflowFilters: [WorkflowFilterInput]
        $CustomerCountry: String
        $CustomerState: String
        $Action: Int
    ) {
        GetInactiveTasksForCompany(
            Filter: {
                DisplayName: $DisplayName
                CustomerCode: $CustomerCode
                InvoiceNumber: $InvoiceNumber
                AmountValue: $AmountValue
                AmountOperator: $AmountOperator
                ActionDateMin: $ActionDateMin
                ActionDateMax: $ActionDateMax
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                Type: $Type
                Automation: $Automation
                Contact: $Contact
                Status: $Status
                TaskIds: $TaskIds
                ExcludeTasks: $ExcludeTasks
                SortField: $SortField
                ThenSortField: $ThenSortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
                CustomFieldFilters: $CustomFieldFilters
                CustomFieldSort: $CustomFieldSort
                AssignedUserId: $AssignedUserId
                AssignedEmailAddress: $AssignedEmailAddress
                WorkflowFilters: $WorkflowFilters
                CustomerCountry: $CustomerCountry
                CustomerState: $CustomerState
                Action: $Action
            }
        ) {
            Tasks {
                ActionDate
                Automation
                ConversationLine {
                    Content
                }
                Customer {
                    Contacts {
                        ${contactFragmentString}
                    }
                    CustomerCode @skip(if: $IsCloudImportType)
                    CompanyName
                    DisplayName
                    CustomFields {
                        Name
                        Value
                    }
                    ConversationLine {
                        Content
                    }
                    PayerState
                }
                Invoice {
                    Number
                    AmountOwing
                    CustomFields {
                        Name
                        Value
                    }
                    ConversationLine {
                        Content
                    }
                }
                Type
                Workflow {
                    WorkflowId
                    StateName
                    DeliveryMethod
                    WorkflowName @include(if: $IsMultipleWorkflow)
                }
                State
                Ticket {
                    Id
                    TicketNumber
                    TicketOption {
                        Id
                        Type
                        Reason
                    }
                    ConversationLine {
                        Content
                    }
                }
                AssignedUser {
                    GivenName
                    FamilyName
                }
                AssignedEmailAddress
            }
        }
    }
`;

export const archiveTasksSummaryQueryName = `GET_ARCHIVE_TASKS_FOR_COMPANY`;
export const archiveTasksSummaryPageQuery = `
    query ${archiveTasksSummaryQueryName}(
        $DisplayName: String
        $CustomerCode: String
        $InvoiceNumber: String
        $AmountValue: Float
        $AmountOperator: String
        $ActionDateMin: AWSDateTime
        $ActionDateMax: AWSDateTime
        $CreatedDateMin: AWSDateTime
        $CreatedDateMax: AWSDateTime
        $Type: Int
        $Automation: Int
        $Contact: Int
        $Status: Int
        $TaskIds: [ID]
        $ExcludeTasks: Boolean
        $SortField: String
        $ThenSortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
        $CustomFieldFilters: String
        $CustomFieldSort: String
        $AssignedUserId: ID
        $AssignedEmailAddress: String
        $IsCloudImportType: Boolean = false
        $IsMultipleWorkflow: Boolean = false
        $WorkflowFilters: [WorkflowFilterInput]
        $CustomerCountry: String
        $CustomerState: String
        $Action: Int
    ) {
        GetArchiveTasksForCompany(
            Filter: {
                DisplayName: $DisplayName
                CustomerCode: $CustomerCode
                InvoiceNumber: $InvoiceNumber
                AmountValue: $AmountValue
                AmountOperator: $AmountOperator
                ActionDateMin: $ActionDateMin
                ActionDateMax: $ActionDateMax
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                Type: $Type
                Automation: $Automation
                Contact: $Contact
                Status: $Status
                TaskIds: $TaskIds
                ExcludeTasks: $ExcludeTasks
                SortField: $SortField
                ThenSortField: $ThenSortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
                CustomFieldFilters: $CustomFieldFilters
                CustomFieldSort: $CustomFieldSort
                AssignedUserId: $AssignedUserId
                AssignedEmailAddress: $AssignedEmailAddress
                WorkflowFilters: $WorkflowFilters
                CustomerCountry: $CustomerCountry
                CustomerState: $CustomerState
                Action: $Action
            }
        ) {
            Tasks {
                ActionDate
                Automation
                ConversationLine {
                    Content
                }
                Customer {
                    Contacts {
                        ${contactFragmentString}
                    }
                    CustomerCode @skip(if: $IsCloudImportType)
                    CompanyName
                    DisplayName
                    CustomFields {
                        Name
                        Value
                    }
                    ConversationLine {
                        Content
                    }
                    PayerState
                }
                Invoice {
                    Number
                    AmountOwing
                    CustomFields {
                        Name
                        Value
                    }
                    ConversationLine {
                        Content
                    }
                }
                Type
                Workflow {
                    WorkflowId
                    StateName
                    DeliveryMethod
                    WorkflowName @include(if: $IsMultipleWorkflow)
                }
                State
                Ticket {
                    Id
                    TicketNumber
                    TicketOption {
                        Id
                        Type
                        Reason
                    }
                    ConversationLine {
                        Content
                    }
                }
                AssignedUser {
                    GivenName
                    FamilyName
                }
                AssignedEmailAddress
            }
        }
    }
`;

export const paymentsSummaryQueryName = `GET_PAYMENTS_FOR_COMPANY`;
export const paymentsSummaryPageQuery = `
    query ${paymentsSummaryQueryName}(
        $Customer: String
        $CustomerCode: String
        $Result: Int
        $PaidDateMin: String
        $PaidDateMax: String
        $AmountValue: String
        $AmountOperator: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
        $CustomFieldFilters: String
        $CustomFieldSort: String
    ) {
        GetPaymentsForCompany(
            Filter: {
                Customer: $Customer
                CustomerCode: $CustomerCode
                Result: $Result
                PaidDateMin: $PaidDateMin
                PaidDateMax: $PaidDateMax
                AmountValue: $AmountValue
                AmountOperator: $AmountOperator
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
                CustomFieldFilters: $CustomFieldFilters
                CustomFieldSort: $CustomFieldSort
            }
        ) {
            Payments {
                Id
                Result
                PaidDate
                LocalPaidDate
                PaidAmount
                Customer {
                    Id
                    CustomerCode
                    CompanyName
                    DisplayName
                    Contacts {
                        ${contactFragmentString}
                    }
                    CustomFields {
                        Name
                        Value
                    }
                }
                Uri
                Allocations {
                    AmountAllocated
                    InvoiceResource {
                        Id
                        Number
                        InvoiceCode
                    }
                    ToResource {
                        Uri
                    }
                }
                Attachments {
                    ${attachmentFragmentString}
                }
                ConversationLine {
                    Content
                }
                SettlementDate
                TransactionFeeBuyer
                TransactionFeeSeller
            }
        }
    }
`;

export const paymentPlansSummaryQueryName = `GET_PAYMENT_PLANS_FOR_COMPANY`;
export const paymentPlansSummaryPageQuery = `
    query ${paymentPlansSummaryQueryName}(
        $Status: Int
        $CreatedDateMin: String
        $CreatedDateMax: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $IsCloudImportType: Boolean = false
        $PaymentPlanIds: [ID]
        $ExcludePaymentPlans: Boolean = false
        $Customer: String
        $CustomerCode: String
        $CompanyId: ID
    ) {
        GetPaymentPlansForCompany(
            Filter: {
                Status: $Status
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                PaymentPlanIds: $PaymentPlanIds
                ExcludePaymentPlans: $ExcludePaymentPlans
                Customer: $Customer
                CustomerCode: $CustomerCode
                CompanyId: $CompanyId
            }
        ) {
            PaymentPlans {
                Number
                TotalAmount
                AmountRemaining
                RequestedDateTime
                ApprovedDateTime
                ClosedDateTime
                Customer {
                    CustomerCode @skip(if: $IsCloudImportType)
                    CompanyName
                    DisplayName
                }
                State
                TotalSchedules
                PaymentPlanSchedules {
                    Number
                    DueDateTime
                    State
                    TotalAmount
                    AmountRemaining
                }
            }
        }
    }
`;

export const remittanceAdvicesSummaryQueryName = `GET_REMITTANCE_ADVICES_FOR_COMPANY`;
export const remittanceAdvicessSummaryPageQuery = `
    query ${remittanceAdvicesSummaryQueryName}(
        $CompanyId: ID,
        $CustomerId: ID,
        $RemittanceAdviceState: Int, 
        $PaidDateMin: String,
        $PaidDateMax: String,
        $PaidAmountMin: String,
        $PaidAmountMax: String,
        $AmountValue: Float,
        $AmountOperator: String,
        $AmountType: String,
        $Customer: String,
        $CustomerCode: String,
        $CustomFieldFilters: String,
        $CustomFieldSort: String,
        $SortField: String,
        $Ascending: Boolean,
        $Skip: Int,
        $PageSize: Int,
        $IsCloudImportType: Boolean = false,
        $ForReport: Boolean = true
    ) {
        GetRemittanceAdvicesForCompany(
            Filter: {
                CompanyId: $CompanyId,
                CustomerId: $CustomerId,
                RemittanceAdviceState: $RemittanceAdviceState, 
                PaidDateMin: $PaidDateMin,
                PaidDateMax: $PaidDateMax,
                PaidAmountMin: $PaidAmountMin,
                PaidAmountMax: $PaidAmountMax,
                AmountValue: $AmountValue,
                AmountOperator: $AmountOperator,
                AmountType: $AmountType,
                Customer: $Customer,
                CustomerCode: $CustomerCode,
                CustomFieldFilters: $CustomFieldFilters,
                CustomFieldSort: $CustomFieldSort,
                SortField: $SortField,
                Ascending: $Ascending,
                Skip: $Skip,
                PageSize: $PageSize,
                ForReport: $ForReport
            }
        ) {
            RemittanceAdvices {
                Id
                CompanyId
                Customer {
                    ${customerFragmentString}
                }
                Status
                ReceivedDate
                LocalReceivedDate
                PaidDate
                LocalPaidDate
                Amount
                MultipleCustomer
                ConversationLine {
                    Id
                    CompanyId
                    CreatedDateTime
                    Type
                    TypeClassification
                    Content
                    Context
                    DisplayName
                    User {
                        UserId
                        GivenName
                        FamilyName
                        Email
                        PhoneNumber
                    }
                    ExternalEmailAddress
                }
                Uri
                Attachments{
                    ${attachmentFragmentString}
                }
                RemittanceAdviceInvoices {
                    ${remittanceAdviceInvoicesFragmentString}
                }
                RemittanceAdviceCredits {
                    ${remittanceAdviceCreditsFragmentString}
                }
                InvoicesAllocated
                CreditsAllocated
            }
        }
    }
`;
export const transcriptReportQueryName = `GET_CHAT_FOR_COMPANY`;
export const transcriptReportQuery = `
    query ${transcriptReportQueryName}(
        $Type: Int
        $ResourceScopes: [ResourceScopeInput]
        $CreatedDateMin: String
        $CreatedDateMax: String
        $SortField: String
        $Ascending: Boolean
        $Skip: Int
        $PageSize: Int
        $CompanyId: ID
    ) {
        GetConversationResourcesForCompany(
            Filter: {
                Type: $Type
                ResourceScopes: $ResourceScopes
                CreatedDateMin: $CreatedDateMin
                CreatedDateMax: $CreatedDateMax
                SortField: $SortField
                Ascending: $Ascending
                Skip: $Skip
                PageSize: $PageSize
                CompanyId: $CompanyId
            }
        ) {
            ConversationLines {
                Id
                CreatedDateTime
                Type
                TypeClassification
                Content
                DisplayName
                User {
                    UserId
                    GivenName
                    FamilyName
                }
                ExternalEmailAddress
                AttachmentId
                Attachment {
                    Title
                }
            }
        }
    }
`;
